import { useMemo } from 'react';

import { CurrencyFormatter } from '@pp/core/src/formatters/CurrencyFormatter';
import { useHideCurrency } from '@pp/core/src/store/compositeSelectors/useHideCurrency';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { roundNumber } from '@pp/core/src/utils/number';

import { useHighflyerGameStateSlice } from '../../../store/slices/highflyerGame/selectors';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';

import { useValidBetInputValue } from './useValidBetInputValue';

export const useGameBetDisplayAmountConnected = (betId: number): string => {
  const { betInputAmount } = useValidBetInputValue(betId);
  const mulitplier = useHighflyerStateSlice.getMultiplier();
  const cashedAmount = useHighflyerStateSlice.getCashedAmount(betId);
  const { min: minBet } = useBetSlice.betLimits() ?? {};
  const isInDealingInProgress = useHighflyerStateSlice.isDealingInProgress(betId);
  const isStatsOpen = useHighflyerGameStateSlice.toggleStats();
  const currency = useGameStateSlice.getCurrency() ?? '';
  const currentLanguage = useSettingsSlice.currentLanguage();
  const hideCurrency = useHideCurrency();
  const localCashedMul = useHighflyerStateSlice.getLocalCashedMul(betId);

  return useMemo(() => {
    let displayAmount = betInputAmount ?? minBet;
    if (isInDealingInProgress) {
      if (localCashedMul > 0) {
        displayAmount = betInputAmount * localCashedMul;
      } else if (cashedAmount > 0) {
        //Once the bet is cashed, then amount should be cashed amount which comes from GS
        displayAmount = cashedAmount;
      } else if (mulitplier > 0) {
        displayAmount = betInputAmount * Number(mulitplier);
      }
    }

    //Special case for Floating betboard
    if (isStatsOpen && cashedAmount > 0) {
      displayAmount = cashedAmount;
    }
    return CurrencyFormatter.format(roundNumber(displayAmount, 2), currentLanguage, { currency, hideCurrency });
  }, [
    betInputAmount,
    minBet,
    isInDealingInProgress,
    isStatsOpen,
    cashedAmount,
    currentLanguage,
    currency,
    hideCurrency,
    mulitplier,
    localCashedMul,
  ]);
};
